import { ReactNode, MouseEventHandler } from 'react';
import { Calendar } from '../../../icons/untitled';
import { cx } from '../../../utils/cx';

export interface HorizontalEventCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  title: string;
  organizationName?: string;
  imageSrc?: string;
  imageAlt?: string;
  category?: string;
  date?: {
    months: string;
    dates: string;
    year?: string;
  };
  time?: string;
  media?: ReactNode;
  onFavoriteClick?: () => void;
  onClick?: MouseEventHandler<HTMLElement>;
}

export function HorizontalEventCard({
  title,
  organizationName,
  imageSrc,
  imageAlt,
  date,
  time,
  media,
  onFavoriteClick: _onFavoriteClick,
  className,
  onClick,
  ...props
}: HorizontalEventCardProps) {
  const mediaNode =
    media ??
    (imageSrc ? (
      <img
        src={imageSrc}
        alt={imageAlt ?? title}
        className="h-full w-20 object-cover"
      />
    ) : (
      <div className="h-full bg-gray-100" />
    ));

  const content = (
    <>
      <div className="relative w-20 h-20 aspect-square overflow-hidden rounded-md">
        {mediaNode}
      </div>
      <div className="flex flex-col justify-center">
        <h3 className="m-0 text-xs font-semibold text-primary-900">{title}</h3>
        <p className="m-0 text-xs font-normal text-tertiary">
          {organizationName}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1 text-xs font-normal text-gray-500">
          {date && (
            <>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>
                  {date.months} {date.dates}
                  {date.year && `, ${date.year}`}
                </span>
              </div>
            </>
          )}
          {time && (
            <div className="flex items-center gap-1">
              <span>•</span>
              <span>{time}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div
      className={cx(
        'relative p-3 bg-secondary rounded-lg border border-secondary border-solid',
        className
      )}
      {...props}
    >
      {onClick ? (
        <button
          type="button"
          className="flex h-full cursor-pointer gap-3 border-none bg-transparent p-0 text-left"
          onClick={onClick}
        >
          {content}
        </button>
      ) : (
        <div className="flex h-full gap-3">{content}</div>
      )}
    </div>
  );
}
